/* eslint-disable import/no-cycle */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { memo, useMemo } from "react";

import { useFormContext } from "react-hook-form";
import styled from "styled-components";

import { Box, Column, Columns, Inline, Text } from "@origin-digital/ods-core";
import { IconProperty } from "@origin-digital/ods-icons";

import { RouterActions, StyledHeading, UppercaseText } from "@/components";
import { IconRoofArea } from "@/components/icons";

import { requireSolarRoute } from "@/features/SolarForm/utils/requireSolarRoute";
import { PageBaseProps } from "@/types/SolarFormProps";

import { ratResultVerify } from "./content.json";

// @ts-ignore
const StyledBox = styled(Box)(() => ({
  "&&": {
    overflow: "hidden",
    backgroundColor: "transparent",
  },
}));

// @ts-ignore
const PlaceholderImage = styled.div(() => ({
  "&&": {
    backgroundImage: `url("https://www.originenergy.com.au/cdn/images/originenergy/image/upload/q_80,f_auto,c_scale/v1690944734/personalisation/internet_hero-1-1_canstar-award_desktop_2023.png")`,
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    width: "100%",
    height: 180,
  },
}));

// @ts-ignore
const StyledColumn = styled(Column)(() => ({
  "&&": {
    paddingTop: 4,
    paddingBottom: 4,

    "&:nth-child(2n)": {
      borderRight: "1px solid #d9d9d9",
    },
  },
}));

type RATResultsVerifyProps = PageBaseProps & {
  nextExistingSolarRoute: () => Promise<string>;
};

export const RATResultsVerify = memo(
  ({ backRoute, nextRoute, nextExistingSolarRoute }: RATResultsVerifyProps) => {
    const { getValues } = useFormContext();

    const roofImageBase64 = getValues("roofImageBase64");

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const accurassiResponses = useMemo(() => {
      return [
        {
          id: ratResultVerify.roofArea.id,
          icon: <IconRoofArea />,
          label: ratResultVerify.roofArea.label,
          content: `${getValues("accurassiRoofArea")} sqm`,
        },
      ];
    }, []);

    const onNextRoute = () => {
      return requireSolarRoute(getValues("accurassiHasSolarPanel"))
        ? nextExistingSolarRoute()
        : nextRoute();
    };

    return (
      <>
        <Box width="full">
          <StyledHeading variant="h3" tone="neutralDark">
            {ratResultVerify.heading}
          </StyledHeading>
          <StyledBox borderRadius="large">
            {roofImageBase64 ? (
              <PlaceholderImage
                data-id="roof-image-base-64"
                style={{
                  backgroundImage: `url("data:image/png;base64,${getValues(
                    "roofImageBase64"
                  )}")`,
                }}
              />
            ) : (
              <PlaceholderImage data-id="default-roof-image" />
            )}
            <Box paddingX="large" paddingY="medium" backgroundColor="white">
              <UppercaseText weight="medium" tone="neutralLight">
                {ratResultVerify.address.label}
              </UppercaseText>
              <Inline>
                <IconProperty color="neutralDark" />
                <Text
                  weight="medium"
                  tone="neutralDark"
                  data-id={ratResultVerify.address.id}
                >
                  {getValues("address")}
                </Text>
              </Inline>

              <Box marginTop="large" marginBottom="large">
                {accurassiResponses.map(({ id, icon, label, content }) => (
                  <Columns alignY="stretch" key={id} space="small">
                    <StyledColumn width="content">{icon}</StyledColumn>
                    <StyledColumn>
                      <Text tone="neutralDark" weight="medium">
                        {label}
                      </Text>
                    </StyledColumn>
                    <StyledColumn width="1/4">
                      <Text data-id={id}>{content || 0}</Text>
                    </StyledColumn>
                  </Columns>
                ))}
              </Box>
            </Box>
          </StyledBox>
        </Box>
        <RouterActions onBackRoute={backRoute} onNextRoute={onNextRoute} />
      </>
    );
  }
);
